import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Slider,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Avatar,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import CompetitorsList from './CompetitorsList';
import ImportExportTab from '../components/ImportExportTab';
import ApiService from '../services/apiService';
import NumberInputField from '../components/NumberInputField';

// Stilisierte Box für die Schieberegler
const SliderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

// Stilisierter Input für Datei-Upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const Settings = () => {
  console.log('Settings-Komponente initialisiert');
  // Zustand für den aktiven Tab
  const [activeTab, setActiveTab] = useState(0);
  
  // Debug-Effekt für activeTab-Änderungen
  useEffect(() => {
    console.log('activeTab geändert zu:', activeTab);
  }, [activeTab]);
  
  // Zustand für die aktuellen Gebühren
  const [currentFee, setCurrentFee] = useState({
    percentage_fee: 0,
    fixed_fee: 0
  });  // Dieser Code wurde entfernt, da die Import/Export-Funktionalität entfernt wurde

  // Zustand für die Payout-Konditionen
  const [payoutConditions, setPayoutConditions] = useState({
    percentage_fee: 0,
    fixed_fee: 0,
    active_account_fee: 1.00 // Standardwert für die Gebühr pro aktivem Account
  });
  
  // Zustand für die Erfolgsmeldung
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // Zustände für Zahlungsmethoden
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' oder 'edit'
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    percentage_fee: 0,
    fixed_fee: 0,
    logo: null,
    logoPreview: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);

  const fileInputRef = useRef(null);
  // Lade die aktuellen Gebühren und Payout-Konditionen beim Start
  useEffect(() => {
    // Aktuelle Gebühren laden
    ApiService.getCurrentFee()
      .then(data => {
        setCurrentFee(data);
      })
      .catch(error => {
        console.error('Fehler beim Laden der aktuellen Gebühren:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Laden der Gebühreneinstellungen',
          severity: 'error'
        });
      });
      // Payout-Konditionen laden
    ApiService.getPayoutConditions()
      .then(data => {
        if (data && data.length > 0) {
          setPayoutConditions({
            ...data[0],
            active_account_fee: data[0].active_account_fee ?? 1.00 // Verwende 1,00€ als Standardwert, wenn nicht gesetzt
          });
        } else {
          // Wenn keine Payout-Konditionen vorhanden sind, erstelle Standardwerte und speichere sie
          const defaultPayoutCondition = {
            percentage_fee: 0.25,
            fixed_fee: 0.10,
            active_account_fee: 2.00
          };
          
          ApiService.createPayoutCondition(defaultPayoutCondition)
            .then(savedCondition => {
              setPayoutConditions(savedCondition);
            })
            .catch(error => {
              console.error('Fehler beim Erstellen der Standard-Payout-Konditionen:', error);
            });
        }
      })
      .catch(error => {
        console.error('Fehler beim Laden der Payout-Konditionen:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Laden der Payout-Konditionen',
          severity: 'error'
        });
      });
      
    // Zahlungsmethoden laden
    loadPaymentMethods();
  }, []);
  
  // Lade die Zahlungsmethoden
  const loadPaymentMethods = () => {
    ApiService.getPaymentMethods()
      .then(data => {
        // Wenn keine Zahlungsmethoden gefunden wurden, erstellen wir die Standard-Zahlungsmethoden
        if (!data || data.length === 0) {
          const defaultPaymentMethods = [
            {
              name: 'PayPal',
              percentage_fee: 1.95,
              fixed_fee: 0.45
            },
            {
              name: 'Klarna',
              percentage_fee: 2.99,
              fixed_fee: 0.35
            },
            {
              name: 'Stripe',
              percentage_fee: 1.50,
              fixed_fee: 0.25
            }
          ];
          
          // Zahlungsmethoden nacheinander erstellen und speichern
          Promise.all(defaultPaymentMethods.map(method => 
            ApiService.createPaymentMethod(method)
          )).then(savedMethods => {
            setPaymentMethods(savedMethods);
          })
          .catch(error => {
            console.error('Fehler beim Erstellen der Standard-Zahlungsmethoden:', error);
          });
        } else {
          setPaymentMethods(data);
        }
      })
      .catch(error => {
        console.error('Fehler beim Laden der Zahlungsmethoden:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Laden der Zahlungsmethoden',
          severity: 'error'
        });
      });
  };

  // Lade die Zahlungsmethoden beim Start
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  // Handler für Tab-Änderungen
  const handleTabChange = (event, newValue) => {
    console.log('Tab geändert zu:', newValue);
    setActiveTab(newValue);
  };

  // Handler für Änderungen an den Gebührenschiebereglern
  const handleFeeChange = (field) => (event, newValue) => {
    setCurrentFee(prev => ({ ...prev, [field]: newValue }));
  };

  // Handler für manuelle Eingaben in die Textfelder
  const handleInputChange = (field) => (event) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    setCurrentFee(prev => ({ ...prev, [field]: value < 0 ? 0 : value }));
  };

  // Handler für das Speichern der Gebühreneinstellungen
  const handleSaveFees = () => {
    ApiService.updateCurrentFee(currentFee)
      .then(data => {
        setSnackbar({
          open: true,
          message: 'Gebühren erfolgreich aktualisiert',
          severity: 'success'
        });
      })
      .catch(error => {
        console.error('Fehler beim Speichern der Gebühren:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Speichern der Gebühren',
          severity: 'error'
        });
      });
  };

  // Handler für Änderungen an den Payout-Schiebereglern
  const handlePayoutChange = (field) => (event, newValue) => {
    setPayoutConditions(prev => ({ ...prev, [field]: newValue }));
  };

  // Handler für manuelle Eingaben in die Payout-Textfelder
  const handlePayoutInputChange = (field) => (event) => {
    const value = event.target.value === '' ? 0 : parseFloat(event.target.value);
    setPayoutConditions(prev => ({ ...prev, [field]: value < 0 ? 0 : value }));
  };

  // Handler für das Speichern der Payout-Konditionen
  const handleSavePayoutConditions = () => {
    // Da PayoutConditions als Array zurückgegeben wird, aber als einzelnes Objekt gespeichert wird
    const payoutConditionToSave = {
      ...payoutConditions,
      id: payoutConditions.id || 1 // Stelle sicher, dass eine ID vorhanden ist
    };
    
    ApiService.savePayoutCondition(payoutConditionToSave)
      .then(data => {
        setSnackbar({
          open: true,
          message: 'Payout-Konditionen erfolgreich aktualisiert',
          severity: 'success'
        });
      })
      .catch(error => {
        console.error('Fehler beim Speichern der Payout-Konditionen:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Speichern der Payout-Konditionen',
          severity: 'error'
        });
      });
  };

  // Handler für das Schließen der Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handler für das Öffnen des Zahlungsmethoden-Dialogs (Hinzufügen)
  const handleOpenAddDialog = () => {
    setFormData({
      id: null,
      name: '',
      percentage_fee: 0,
      fixed_fee: 0,
      logo: null,
      logoPreview: null
    });
    setDialogMode('add');
    setDialogOpen(true);
  };

  // Handler für das Bearbeiten einer Zahlungsmethode
  const handleEditPaymentMethod = (method) => {
    setDialogMode('edit');
    setFormData({
      id: method.id,
      name: method.name,
      percentage_fee: method.percentage_fee,
      fixed_fee: method.fixed_fee,
      logo: null,
      logoPreview: method.logo_path
    });
    setDialogOpen(true);
  };
  // Handler für Änderungen am Formular
  const handleFormChange = (field) => (event) => {
    // Für numerische Felder den Wert als Zahl umwandeln
    const value = ['percentage_fee', 'fixed_fee'].includes(field) 
      ? parseFloat(event.target.value) 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handler für Logo-Upload
  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const base64 = await ApiService.saveImageAsBase64(file);
      setFormData(prev => ({
        ...prev,
        logo: file,
        logoPreview: base64
      }));
    } catch (error) {
      console.error('Fehler beim Logo-Upload:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Hochladen des Logos',
        severity: 'error'
      });
    }
  };

  // Handler für das Schließen des Dialogs
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      id: null,
      name: '',
      percentage_fee: 0,
      fixed_fee: 0,
      logo: null,
      logoPreview: null
    });
  };

  // Handler für das Absenden des Formulars
  const handleSubmitForm = (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Formular-Daten vorbereiten
    const formDataToSave = {
      ...formData,
      logo_path: formData.logoPreview  // Verwende logoPreview als logo_path
    };
    
    // API-Aufruf basierend auf dem Modus (hinzufügen oder bearbeiten)
    const savePromise = dialogMode === 'add'
      ? ApiService.createPaymentMethod(formDataToSave)
      : ApiService.updatePaymentMethod(formData.id, formDataToSave);
    
    savePromise
      .then(response => {
        // Dialog schließen und Formular zurücksetzen
        setDialogOpen(false);
        setFormData({
          id: null,
          name: '',
          percentage_fee: 0,
          fixed_fee: 0,
          logo: null,
          logoPreview: null
        });
        
        // Liste der Zahlungsmethoden neu laden
        loadPaymentMethods();
        
        // Erfolgsmeldung anzeigen
        setSnackbar({
          open: true,
          message: dialogMode === 'add' 
            ? 'Zahlungsmethode erfolgreich hinzugefügt' 
            : 'Zahlungsmethode erfolgreich aktualisiert',
          severity: 'success'
        });
      })
      .catch(error => {
        console.error('Fehler beim Speichern der Zahlungsmethode:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Speichern der Zahlungsmethode',
          severity: 'error'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Handler für das Öffnen des Löschdialogs
  const handleOpenDeleteDialog = (method) => {
    setPaymentMethodToDelete(method);
    setDeleteDialogOpen(true);
  };

  // Handler für das Bestätigen des Löschens
  const handleConfirmDelete = () => {
    if (!paymentMethodToDelete) return;

    ApiService.deletePaymentMethod(paymentMethodToDelete.id)
      .then(success => {
        if (success) {
          loadPaymentMethods();
          setSnackbar({
            open: true,
            message: 'Zahlungsmethode erfolgreich gelöscht',
            severity: 'success'
          });
        } else {
          throw new Error('Löschen fehlgeschlagen');
        }
      })
      .catch(error => {
        console.error('Fehler beim Löschen der Zahlungsmethode:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Löschen der Zahlungsmethode',
          severity: 'error'
        });
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setPaymentMethodToDelete(null);
      });
  };
  // Export/Import-Funktionalität wurde entfernt, da sie jetzt im Zahlungsmethoden-Tab integriert ist

  return (
    <>      <Box sx={{ width: '100%', bgcolor: 'background.paper', mb: 3 }}>        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >          <Tab label="Gebühren" />
          <Tab label="Auszahlung" />
          <Tab label="Wettbewerber" />
          <Tab label="Zahlungsmethoden" />
          <Tab label="Im-/Export" />
        </Tabs>
      </Box>

      {/* Gebühren-Tab */}
      {activeTab === 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Gebühreneinstellungen
          </Typography>
          
          <Paper elevation={2} sx={{ mt: 2 }}>
            <SliderBox>
              <Typography gutterBottom>
                Prozentuale Gebühr: {currentFee.percentage_fee}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={currentFee.percentage_fee}
                  onChange={handleFeeChange('percentage_fee')}
                  min={0.10}
                  max={15.00}
                  step={0.1}
                  valueLabelDisplay="auto"
                  sx={{ flexGrow: 1 }}
                />                <NumberInputField
                  value={currentFee.percentage_fee}
                  onChange={handleInputChange('percentage_fee')}
                  size="small"
                  min={0}
                  step={0.1}
                  endAdornment="%"
                  sx={{ width: 100 }}
                />
              </Box>
            </SliderBox>

            <SliderBox>
              <Typography gutterBottom>
                Fixe Gebühr: {currentFee.fixed_fee} €
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={currentFee.fixed_fee}
                  onChange={handleFeeChange('fixed_fee')}
                  min={0}
                  max={10}
                  step={0.01}
                  valueLabelDisplay="auto"
                  sx={{ flexGrow: 1 }}
                />                <NumberInputField
                  value={currentFee.fixed_fee}
                  onChange={handleInputChange('fixed_fee')}
                  size="small"
                  min={0}
                  step={0.01}
                  endAdornment="€"
                  sx={{ width: 100 }}
                />
              </Box>
            </SliderBox>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSaveFees}
            >
              Speichern
            </Button>
          </Box>
        </Paper>
      )}

      {/* Auszahlung-Tab */}
      {activeTab === 1 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Auszahlungseinstellungen
          </Typography>
          
          <Paper elevation={2} sx={{ mt: 2 }}>
            <SliderBox>
              <Typography gutterBottom>
                Prozentuale Gebühr: {payoutConditions.percentage_fee}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={payoutConditions.percentage_fee}
                  onChange={handlePayoutChange('percentage_fee')}
                  min={0.10}
                  max={15.00}
                  step={0.1}
                  valueLabelDisplay="auto"
                  sx={{ flexGrow: 1 }}
                />                <NumberInputField
                  value={payoutConditions.percentage_fee}
                  onChange={handlePayoutInputChange('percentage_fee')}
                  size="small"
                  min={0}
                  step={0.1}
                  endAdornment="%"
                  sx={{ width: 100 }}
                />
              </Box>
            </SliderBox>

            <SliderBox>
              <Typography gutterBottom>
                Fixe Gebühr: {payoutConditions.fixed_fee} €
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={payoutConditions.fixed_fee}
                  onChange={handlePayoutChange('fixed_fee')}
                  min={0}
                  max={10}
                  step={0.01}
                  valueLabelDisplay="auto"
                  sx={{ flexGrow: 1 }}                />                <NumberInputField
                  value={payoutConditions.fixed_fee}
                  onChange={handlePayoutInputChange('fixed_fee')}
                  size="small"
                  min={0}
                  step={0.01}
                  endAdornment="€"
                  sx={{ width: 100 }}
                />
              </Box>
            </SliderBox>

            <SliderBox>
              <Typography gutterBottom>
                Gebühr pro aktivem Account: {payoutConditions.active_account_fee} €
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={payoutConditions.active_account_fee}
                  onChange={handlePayoutChange('active_account_fee')}
                  min={0}
                  max={10}
                  step={0.01}
                  valueLabelDisplay="auto"
                  sx={{ flexGrow: 1 }}
                />                <NumberInputField
                  value={payoutConditions.active_account_fee}
                  onChange={handlePayoutInputChange('active_account_fee')}
                  size="small"
                  min={0}
                  step={0.01}
                  endAdornment="€"
                  sx={{ width: 100 }}
                />
              </Box>
            </SliderBox>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSavePayoutConditions}
            >
              Speichern
            </Button>
          </Box>
        </Paper>
      )}

      {/* Wettbewerber-Tab */}
      {activeTab === 2 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Wettbewerber verwalten
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/competitors/new"
            sx={{ mb: 2 }}
          >
            Neuer Wettbewerber
          </Button>

          {/* Eingebettete CompetitorsList-Komponente */}
          <CompetitorsList embedded={true} />
        </Paper>      )}      {/* Zahlungsmethoden-Tab */}
      {activeTab === 3 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Zahlungsmethoden verwalten
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Neue Zahlungsmethode
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Logo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Gebühren</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentMethods && paymentMethods.length > 0 
                  ? paymentMethods.map((method) => (
                    <TableRow key={method.id}>
                      <TableCell>
                        {method.logo_path ? (
                          <Avatar
                            src={method.logo_path}
                            alt={`${method.name} Logo`}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}
                          >
                            {method.name.charAt(0)}
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>{method.name}</TableCell>
                      <TableCell>
                        {method.percentage_fee.toFixed(2)}% + {method.fixed_fee.toFixed(2)} €
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEditPaymentMethod(method)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={() => handleOpenDeleteDialog(method)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                  : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography>
                          Keine Zahlungsmethoden vorhanden
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>      )}

      {/* Import/Export-Tab */}
      {activeTab === 4 && <ImportExportTab />}

      {/* Dialog zum Hinzufügen/Bearbeiten von Zahlungsmethoden */}
      <Dialog
        open={dialogOpen}
        onClose={isSubmitting ? undefined : handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitForm}>
          <DialogTitle>
            {dialogMode === 'add' ? 'Neue Zahlungsmethode' : 'Zahlungsmethode bearbeiten'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              {/* Logo Upload */}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar
                    src={formData.logoPreview}
                    variant="rounded"
                    alt="Logo Vorschau"
                    sx={{ width: 100, height: 100, mb: 1 }}
                  >
                    {formData.logoPreview ? null : <PhotoCameraIcon />}
                  </Avatar>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    size="small"
                  >
                    Logo auswählen
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/png"
                      onChange={handleLogoChange} 
                      ref={fileInputRef}
                    />
                  </Button>
                </Box>
              </Grid>

              {/* Name der Zahlungsmethode */}
              <Grid item xs={12}>
                <TextField
                  label="Name der Zahlungsmethode"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleFormChange('name')}
                />
              </Grid>

              {/* Prozentuale Gebühr */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Prozentuale Gebühr (%)"
                  fullWidth
                  type="number"
                  required                  value={formData.percentage_fee}
                  onChange={handleFormChange('percentage_fee')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>

              {/* Fixe Gebühr */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fixe Gebühr (€)"
                  fullWidth
                  type="number"
                  required
                  value={formData.fixed_fee}
                  onChange={handleFormChange('fixed_fee')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog zum Löschen einer Zahlungsmethode */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Zahlungsmethode löschen
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie die Zahlungsmethode "{paymentMethodToDelete?.name}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained" 
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Settings;